// components/playground/sections/ScenarioSidebar.jsx
import React, { useState, useMemo } from 'react';
import { MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import ScenarioListItem from './ScenarioListItem';

const ScenarioSidebar = ({
  scenarios,
  currentScenarioId,
  isActualView,
  hasUnsavedChanges,
  currentYear,
  currentMonth,
  onLoadActual,
  onLoadScenario,
  onNewScenario,
  formatRelativeTime,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false); // ✅ Add collapsed state

  const filteredScenarios = useMemo(() => {
    if (!searchQuery.trim()) return scenarios;
    
    const query = searchQuery.toLowerCase();
    return scenarios.filter(scenario => 
      scenario.scenario_name.toLowerCase().includes(query) ||
      (scenario.scenario_description || '').toLowerCase().includes(query)
    );
  }, [scenarios, searchQuery]);

  // ✅ Collapsed view - just a toggle button
  if (isCollapsed) {
    return (
      <div className="w-12 bg-gray-50 border-l border-gray-200 flex items-start justify-center pt-4">
        <button
          onClick={() => setIsCollapsed(false)}
          className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          title="Expand sidebar"
        >
          <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
        </button>
      </div>
    );
  }

  // ✅ Expanded view - full sidebar
  return (
    <div className="w-80 bg-gray-50 border-l border-gray-200 flex flex-col">
      {/* Sidebar Header */}
      <div className="px-4 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
            Scenarios
          </h3>
          {/* ✅ Collapse button */}
          <button
            onClick={() => setIsCollapsed(true)}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
            title="Collapse sidebar"
          >
            <ChevronRightIcon className="w-4 h-4 text-gray-600" />
          </button>
        </div>
        
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search scenarios..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Scenario List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2" style={{ maxHeight: 'calc(90vh - 200px)' }}>
        {/* Actual Data */}
        <button
          onClick={onLoadActual}
          className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
            isActualView
              ? 'bg-blue-100 border-2 border-blue-500'
              : 'bg-white border-2 border-transparent hover:border-blue-300'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900">Actual Data</div>
              <div className="text-xs text-gray-500">Baseline reference</div>
            </div>
            {isActualView && (
              <div className="text-blue-600 font-bold">✓</div>
            )}
          </div>
        </button>

        {/* Divider */}
        {filteredScenarios.length > 0 && (
          <div className="py-2">
            <div className="border-t border-gray-300"></div>
            <div className="text-xs text-gray-500 uppercase mt-2 px-2 flex items-center justify-between">
              <span>Saved</span>
              <span className="text-gray-400">
                {filteredScenarios.length} {filteredScenarios.length !== scenarios.length && `of ${scenarios.length}`}
              </span>
            </div>
          </div>
        )}

        {/* Saved Scenarios */}
        {filteredScenarios.length > 0 ? (
          filteredScenarios.map(scenario => (
            <ScenarioListItem
              key={scenario.scenario_id}
              scenario={scenario}
              isActive={currentScenarioId === scenario.scenario_id}
              hasUnsavedChanges={hasUnsavedChanges}
              currentYear={currentYear}
              currentMonth={currentMonth}
              onClick={() => onLoadScenario(scenario.scenario_id)}
              formatRelativeTime={formatRelativeTime}
            />
          ))
        ) : searchQuery && (
          <div className="text-center py-8 text-sm text-gray-500">
            No scenarios match "{searchQuery}"
          </div>
        )}
      </div>

      {/* Sidebar Footer */}
      <div className="p-4 border-t border-gray-200">
        <button
          onClick={onNewScenario}
          className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          + New Scenario
        </button>
      </div>
    </div>
  );
};

export default ScenarioSidebar;